import React, { useState } from 'react';
import { ArrowUp, X } from 'lucide-react';
import Button from '../common/Button';

const Footer = () => {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const services = [
    'Mumbai – Bangalore Parcel Services',
    'Mumbai – Hyderabad Logistics',
    'Full Truckload | Partial Load',
    'Fast Delivery : Mumbai – Bangalore',
    'Parcel (Road Transport)',
    'Next Day Delivery – Mumbai to Bangalore',
    'Cost-effective cargo transport solutions',
    'Regular Mumbai–Bangalore Cargo Movement',
    'Cargo Mumbai to Bangalore'
  ];

  return (
    <>
      <button
        onClick={scrollToTop}
        className="fixed bottom-6 right-6 bg-brand hover:bg-brand-hover text-white p-3 rounded-full shadow-medium hover:shadow-strong transition-all duration-300 z-40"
        aria-label="Scroll to top"
      >
        <ArrowUp className="w-5 h-5" />
      </button>

      <footer className="relative bg-dark text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]">
          <svg className="w-full h-full" viewBox="0 0 1200 600" fill="none" xmlns="http://www.w3.org/2000/svg">
            {Array.from({ length: 60 }).map((_, i) => (
              <circle key={i} cx={(i % 12) * 100} cy={Math.floor(i / 12) * 100} r="2" fill="white" />
            ))}
            <path d="M50 150 C300 50, 600 250, 900 120" stroke="white" strokeWidth="2" strokeDasharray="6 8" />
            <path d="M100 400 C400 300, 700 450, 1100 350" stroke="white" strokeWidth="2" strokeDasharray="6 8" />
            <circle cx="50" cy="150" r="6" fill="#2563eb" />
            <circle cx="900" cy="120" r="6" fill="#60a5fa" />
            <circle cx="100" cy="400" r="6" fill="#2563eb" />
            <circle cx="1100" cy="350" r="6" fill="#60a5fa" />
          </svg>
        </div>

        <div className="relative container mx-auto px-4 py-12 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
            <div className="lg:col-span-5">
              <h3 className="text-2xl font-semibold text-brand-light mb-3">
                Handled with Care, Delivered with Pride
              </h3>
              <p className="text-gray-300 font-medium mb-4">
                Powered by Technology, Driven by Service.
              </p>
              <p className="text-gray-400 leading-relaxed text-sm">
                At Metro Logistic, we move more than cargo—we move trust. Our end-to-end logistics solutions are designed to keep your shipments fast, secure, and fully trackable. From local deliveries to long-haul transport, we make logistics simple and stress-free.
              </p>
            </div>

            <div className="lg:col-span-4">
              <h3 className="text-lg font-semibold text-white mb-5">
                Key Routes & Services
              </h3>
              <ul className="space-y-2.5 text-gray-400 text-sm">
                {services.map((service, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand mt-2 shrink-0" />
                    {service}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-3">
              <h3 className="text-lg font-semibold text-white mb-5">
                Contact Info
              </h3>
              <div className="space-y-4 text-gray-400 text-sm">
                <p>
                  <span className="text-gray-300 font-medium block mb-1">Head Office</span>
                  Shivaji Co-Operative Housing Society LTD,<br />
                  Plot No 13, Crystal Compound<br />
                  Sion Trombay Road, Sion, Mumbai-400022
                </p>
                <p>
                  <span className="text-gray-300 font-medium block mb-1">Phone</span>
                  +91 94805 01861
                </p>
                <p>
                  <span className="text-gray-300 font-medium block mb-1">Email</span>
                  admin@metrologistics.co.in
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative border-t border-gray-700 bg-gray-900/80">
          <div className="container mx-auto px-4 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              Designed with Passion by <span className="text-brand">DigiQin</span> © 2025{' '}
              <span className="text-gray-300 font-medium">Metro Logistics</span>. All rights reserved.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" size="sm" className="!text-gray-300 !border-gray-600 hover:!bg-gray-800 hover:!text-white" onClick={() => setShowPrivacyModal(true)}>
                Privacy Policy
              </Button>
              <Button variant="outline" size="sm" className="!text-gray-300 !border-gray-600 hover:!bg-gray-800 hover:!text-white" onClick={() => setShowTermsModal(true)}>
                Terms of Service
              </Button>
            </div>
          </div>
        </div>
      </footer>

      {showPrivacyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-2xl rounded-card shadow-strong overflow-hidden">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-dark">Privacy Policy</h3>
              <button onClick={() => setShowPrivacyModal(false)} className="p-2 rounded-btn hover:bg-gray-100 transition" aria-label="Close">
                <X className="w-5 h-5 text-secondary" />
              </button>
            </div>
            <div className="px-6 py-6 max-h-[70vh] overflow-y-auto space-y-4 text-secondary leading-relaxed text-sm">
              <p>
                <span className="font-semibold text-dark">Metro Logistics</span> values your privacy. We collect basic personal and shipment-related information to provide logistics services, communicate updates, and improve our website.
              </p>
              <p>
                We do not sell or misuse your data. Information may be shared only with authorized partners or government authorities when legally required. Reasonable security measures are used to protect your information.
              </p>
              <p>By using our website, you agree to this Privacy Policy. This policy may be updated from time to time.</p>
              <div className="border-t pt-4">
                <p className="font-semibold text-dark mb-2">For any questions, contact us:</p>
                <p>admin@metrologistics.co.in</p>
                <p>+91 99691 13548</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-2xl rounded-card shadow-strong overflow-hidden">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-dark">Terms of Service</h3>
              <button onClick={() => setShowTermsModal(false)} className="p-2 rounded-btn hover:bg-gray-100 transition" aria-label="Close">
                <X className="w-5 h-5 text-secondary" />
              </button>
            </div>
            <div className="px-6 py-6 max-h-[70vh] overflow-y-auto space-y-4 text-secondary leading-relaxed text-sm">
              <p>
                By using this website and our logistics services, you agree to comply with these <span className="font-semibold text-dark">Terms of Service</span>.
              </p>
              <p>
                All shipment details provided by customers must be accurate. <span className="font-semibold text-dark">Metro Logistics</span> is not responsible for delays, losses, or damages caused by incorrect information, natural events, or circumstances beyond our control.
              </p>
              <p>Service charges, delivery timelines, and terms may change without prior notice. We reserve the right to refuse service if these terms are violated.</p>
              <p>Use of this website indicates acceptance of these terms.</p>
              <div className="border-t pt-4">
                <p className="font-semibold text-dark mb-2">For queries, contact us:</p>
                <p>admin@metrologistics.co.in</p>
                <p>+91 99691 13548</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
